import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerVentasDelDia } from '../services/ventasDiaService';
import { listarEquipo } from '../services/equipoService';
import { fechaLocal } from '../utils/fecha';
import { mensajeAmigable } from '../utils/errores';
import ComprobanteModal from './ComprobanteModal';

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hora = (iso) => new Date(iso).toLocaleTimeString('es-HN', { hour: 'numeric', minute: '2-digit' });

// "Ventas de hoy": cada venta del día con hora, forma de pago y quién la hizo.
// La vendedora ve solo las suyas, salvo que tenga el permiso "ventas_todas".
export default function VentasHoyView({ onVolver }) {
  const { sesion, esVendedora, puede } = useAuth();
  const yo = sesion?.user?.id;
  const verTodas = puede('ventas_todas');
  const verCostos = puede('costos');
  const [fecha, setFecha] = useState(fechaLocal());
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState(null);
  const [nombres, setNombres] = useState({}); // user_id → correo (solo la dueña)
  const [ticketComprobante, setTicketComprobante] = useState(null);

  useEffect(() => {
    let activo = true;
    setFilas(null); setError(null);
    obtenerVentasDelDia(fecha, verTodas ? null : yo)
      .then((d) => activo && setFilas(d))
      .catch((e) => activo && setError(mensajeAmigable(e)));
    return () => { activo = false; };
  }, [fecha, verTodas, yo]);

  useEffect(() => {
    if (esVendedora) return;
    listarEquipo().then((eq) => {
      const m = {};
      (eq?.miembros || []).forEach((x) => { m[x.user_id] = x.soy_yo ? 'Tú' : x.email.split('@')[0]; });
      setNombres(m);
    }).catch(() => {});
  }, [esVendedora]);

  const quien = (id) => (!id ? '—' : id === yo ? 'Tú' : (nombres[id] || 'Equipo'));
  // Los envíos no son venta ni ganancia: entran y salen al pagarle al courier.
  const ventasSinEnvio = (filas || []).filter((f) => !f.envio);
  const envios = (filas || []).filter((f) => f.envio);
  const total = ventasSinEnvio.reduce((s, f) => s + f.monto, 0);
  const ganancia = ventasSinEnvio.reduce((s, f) => s + f.monto - f.costo, 0);
  const totalEnvios = envios.reduce((s, f) => s + f.monto, 0);
  const numVentas = new Set(ventasSinEnvio.map((f) => f.ticket || f.id)).size;
  const esHoy = fecha === fechaLocal();

  return (
    <div className="k-page">
      <div className="k-page-head">
        <h2 className="k-page-title">🛒 {esHoy ? 'Ventas de hoy' : 'Ventas del día'}</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" value={fecha} max={fechaLocal()} onChange={(e) => e.target.value && setFecha(e.target.value)}
            aria-label="Día" style={{ padding: '8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontFamily: 'inherit' }} />
          <button onClick={onVolver} className="k-btn-soft">← Volver</button>
        </div>
      </div>

      {!verTodas && <p style={{ margin: '0 0 12px', color: '#6B7280', fontSize: '13px' }}>Estas son las ventas que tú registraste.</p>}

      <div className="k-kpis" style={{ marginBottom: '16px' }}>
        <div className="k-tile"><span className="lbl">Total vendido</span><span className="num">{fmt(total)}</span><span className="sub">{numVentas} venta(s)</span></div>
        {verCostos && <div className="k-tile"><span className="lbl">Ganancia estimada</span><span className="num" style={{ color: 'var(--k-verde, #0D9488)' }}>{fmt(ganancia)}</span><span className="sub">Venta menos costo</span></div>}
        {envios.length > 0 && <div className="k-tile"><span className="lbl">🚚 Envíos cobrados</span><span className="num" style={{ color: '#0EA5E9' }}>{fmt(totalEnvios)}</span><span className="sub">{envios.length} envío(s) · son para el courier, no se suman a la venta</span></div>}
      </div>

      {error && <p style={{ color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>}
      {!filas && !error && <p style={{ color: '#6B7280' }}>⏳ Cargando ventas…</p>}
      {filas && filas.length === 0 && (
        <div className="k-card" style={{ padding: '28px', textAlign: 'center', color: '#6B7280' }}>
          {esHoy ? 'Todavía no hay ventas hoy. Las ventas que registres aparecerán aquí al momento.' : 'No hubo ventas ese día.'}
        </div>
      )}
      {filas && filas.length > 0 && (
        <div className="k-card" style={{ padding: '4px 16px' }}>
          {filas.map((f, i) => (
            <div key={f.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #F3F0FA', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '13px', color: '#6B7280', minWidth: '64px' }}>{hora(f.hora)}</span>
              <div style={{ flex: 1, minWidth: '160px' }}>
                <div style={{ fontWeight: 600 }}>{f.descripcion}</div>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  {f.envio ? 'Envío · para el courier' : f.tipo === 'libre' ? 'Venta libre' : 'Producto'}{f.metodo ? ` · ${f.metodo}` : ''}{(verTodas || !esVendedora) ? ` · ${quien(f.quien)}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 700, fontFamily: "'DM Mono', monospace", color: f.envio ? '#6B7280' : undefined }}>{fmt(f.monto)}</div>
                {verCostos && f.costo > 0 && <div style={{ fontSize: '12px', color: 'var(--k-verde, #0D9488)' }}>gana {fmt(f.monto - f.costo)}</div>}
              </div>
              {/* Un botón por venta (los productos de una misma venta comparten ticket). */}
              {f.ticket && filas.findIndex((x) => x.ticket === f.ticket) === i && (
                <button onClick={() => setTicketComprobante(f.ticket)} className="k-btn-soft" title="Comprobante de venta" style={{ padding: '6px 10px' }}>🧾</button>
              )}
            </div>
          ))}
        </div>
      )}
      {ticketComprobante && <ComprobanteModal ticket={ticketComprobante} onCerrar={() => setTicketComprobante(null)} />}
    </div>
  );
}
